import { useCallback, useEffect, useState } from 'react';
import { api, tokens } from '../../lib/api';

/**
 * KASA · BANKA · EMANET — ekranların ortak tipleri ve küçük yardımcıları.
 * Kurallar sunucuda (`apps/api/src/kasa.ts`); burası yalnız gösterir.
 */
export type Para = 'TRY' | 'EUR' | 'USD';
export type HesapTur = 'KASA' | 'BANKA' | 'EMANET';

export type Hesap = {
  id: number; ad: string; tur: HesapTur; resmi: boolean; para: Para;
  bankaAdi: string | null; sube: string | null; iban: string | null; hesapNo: string | null; hesapSahibi: string | null;
  acilisKurus: number; acilisTarihi: string | null; not: string | null; aktif: boolean; sira: number;
  disHesapId: string | null; disBakiyeKurus: number | null; disBakiyeAt: string | null;
  girisKurus: number; cikisKurus: number; bakiyeKurus: number; farkKurus: number | null;
  sonHareket: string | null; hareketAdet: number;
};
export type Kategori = {
  id: number; ad: string; yon: 'GIRIS' | 'CIKIS' | 'IKISI'; grup: string; emanet: boolean; cariZorunlu: boolean;
  aktif: boolean; sira: number; sistem: boolean; kod: string | null; kullanim: number;
};
export type Hareket = {
  id: number; hesapId: number; hesapAd: string; hesapResmi: boolean; para: Para; tarih: string; yon: 'GIRIS' | 'CIKIS'; tutarKurus: number;
  kategoriId: number; kategoriAd: string; kategoriGrup: string; emanet: boolean;
  customerId: number | null; supplierId: number | null; cariAd: string | null; kisi: string | null;
  aciklama: string | null; belgeNo: string | null; vadeTarihi: string | null;
  orderId: number | null; orderNo: string | null; receivableId: number | null;
  virmanId: string | null; karsiHesap: string | null;
  iptal: boolean; iptalAt: string | null; iptalNeden: string | null; iptalEden: string | null;
  olusturan: string | null; createdAt: string; yuruyenKurus: number | null;
};
export type EmanetSatiri = {
  anahtar: string; tur: 'MUSTERI' | 'TEDARIKCI' | 'KISI'; ad: string; customerId: number | null; supplierId: number | null;
  para: Para; girisKurus: number; cikisKurus: number; netKurus: number; yon: 'BIZDE' | 'ALACAK' | 'KAPALI'; sonTarih: string | null; adet: number;
};
export type EmanetToplam = { para: Para; bizdeKurus: number; alacakKurus: number; kisiAdet: number };
export type TurToplami = { tur: HesapTur; resmi: boolean; para: Para; kurus: number; adet: number };
export type Ozet = {
  hesaplar: Hesap[]; toplamlar: TurToplami[]; emanet: EmanetToplam[]; bugun: string;
  gayriGorur: boolean; muhasebeAd: string; muhasebeKip: 'mock' | 'real' | 'off';
};
export type DisHesap = {
  disId: string; ad: string; tur: 'KASA' | 'BANKA'; para: string; bakiyeKurus: number;
  bankaAdi?: string | null; sube?: string | null; hesapNo?: string | null; iban?: string | null; bagliHesapId: number | null;
};

export const TUR_AD: Record<HesapTur, string> = { KASA: 'Kasa', BANKA: 'Banka hesabı', EMANET: 'Emanet hesabı' };
export const YON_AD = { GIRIS: 'Giriş', CIKIS: 'Çıkış', IKISI: 'Giriş ve çıkış' } as const;
export const PARA_SIMGE: Record<string, string> = { TRY: '₺', EUR: '€', USD: '$' };

/** Tutar, hesabın kendi para biriminde. Kuruş varsa gösterilir. */
export function paraYaz(kurus: number, para: string = 'TRY'): string {
  const tam = Math.round(kurus) % 100 === 0;
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency', currency: para, minimumFractionDigits: tam ? 0 : 2, maximumFractionDigits: tam ? 0 : 2,
  }).format(kurus / 100);
}

/** "2026-10-03" → "03.10.2026" */
export const gunYaz = (g: string | null | undefined) => (g ? `${g.slice(8, 10)}.${g.slice(5, 7)}.${g.slice(0, 4)}` : '—');

/** Bugün (Türkiye saati) — `YYYY-AA-GG`. */
export const bugunIso = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul' }).format(new Date());

/** Çift tıklamaya karşı işlem anahtarı. */
export function yeniAnahtar(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** IBAN'ı dörderli öbeklerle gösterir. */
export const ibanYaz = (iban: string | null) => (iban ? iban.replace(/(.{4})/g, '$1 ').trim() : '');

/** Türkiye IBAN'ı: TR + 24 rakam ve mod 97. Sunucudaki denetimin aynısı (yazarken uyarmak için). */
export function ibanGecerli(iban: string): boolean {
  const s = iban.replace(/\s+/g, '').toUpperCase();
  if (!/^TR\d{24}$/.test(s)) return false;
  const dizi = (s.slice(4) + s.slice(0, 4)).replace(/[A-Z]/g, (h) => String(h.charCodeAt(0) - 55));
  let kalan = 0;
  for (const r of dizi) kalan = (kalan * 10 + Number(r)) % 97;
  return kalan === 1;
}

/** Hesap seçeneği (AraSec): ad + tür, para birimi, bakiye. */
export const hesapSecenek = (h: Hesap) => ({
  id: h.id, ad: h.ad,
  alt: `${TUR_AD[h.tur]}${h.resmi ? '' : ' · gayriresmî'} · ${paraYaz(h.bakiyeKurus, h.para)}`,
});

/** Hesap ve kategori tanımları: çekmeceler ve sekmeler aynı listeyi kullanır. */
export function useKasaTanimlari(acik = true) {
  const [hesaplar, setHesaplar] = useState<Hesap[] | null>(null);
  const [kategoriler, setKategoriler] = useState<Kategori[] | null>(null);
  const [gruplar, setGruplar] = useState<string[]>([]);
  const [hata, setHata] = useState<string | null>(null);
  const yukle = useCallback(async () => {
    if (!acik) return;
    try {
      const [h, k] = await Promise.all([
        api<{ hesaplar: Hesap[] }>('/api/kasa/hesap'),
        api<{ kategoriler: Kategori[]; gruplar: string[] }>('/api/kasa/kategori'),
      ]);
      setHesaplar(h.hesaplar); setKategoriler(k.kategoriler); setGruplar(k.gruplar); setHata(null);
    } catch (e) { setHata(e instanceof Error ? e.message : 'Tanımlar okunamadı'); }
  }, [acik]);
  useEffect(() => { void yukle(); }, [yukle]);
  return { hesaplar, kategoriler, gruplar, hata, yukle };
}

/** Excel dökümünü oturum jetonuyla indirir (bağlantıya jeton yazılmaz). */
export async function kasaCsvIndir(sorgu: string): Promise<void> {
  const cagir = () => fetch(`/api/kasa/hareket.csv?${sorgu}`, { headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {} });
  let r = await cagir();
  if (r.status === 401) { await api('/api/me').catch(() => undefined); r = await cagir(); }
  if (!r.ok) throw new Error('Dosya hazırlanamadı');
  const ad = /filename="([^"]+)"/.exec(r.headers.get('content-disposition') ?? '')?.[1] ?? 'kasa-hareketleri.csv';
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url; a.download = ad; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
